import { Toolbar } from '@/components/Toolbar';
import { ComponentProps, forwardRef, ReactNode, useEffect, useState } from 'react';

interface Props {
    id: string;
    label: string;
    icon: ReactNode;
    title?: string;
    expandedTitle?: string;
    onToggle?: (expanded: boolean) => void;
    children: ComponentProps<typeof Toolbar>['children'];
}

const MapControlMenu = forwardRef<HTMLButtonElement, Props>(
    (
        { id, label, icon, title = label, expandedTitle = title, onToggle, children },
        ref
    ) => {
        const [expanded, setExpanded] = useState(false);
        const currentTitle = expanded ? expandedTitle : title;

        const setMenuExpanded = (nextExpanded: boolean) => {
            setExpanded(nextExpanded);
            onToggle?.(nextExpanded);
        };

        useEffect(() => {
            if (!expanded) return;
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    setExpanded(false);
                    onToggle?.(false);
                }
            };
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }, [expanded, onToggle]);

        return (
            <div className="c-map-control-menu c-map-controls__button">
                <button
                    ref={ref}
                    type="button"
                    className="c-map-control-menu__toggle o-btn-square"
                    title={currentTitle}
                    aria-label={currentTitle}
                    aria-expanded={expanded}
                    aria-controls={id}
                    onClick={() => setMenuExpanded(!expanded)}
                >
                    {icon}
                </button>
                {expanded && (
                    <Toolbar
                        id={id}
                        orientation="horizontal"
                        className="c-map-control-menu__options"
                        ariaLabel={label}
                    >
                        {children}
                    </Toolbar>
                )}
            </div>
        );
    }
);

MapControlMenu.displayName = 'MapControlMenu';
export { MapControlMenu };
